/**
 * Auth Service (Azure AD)
 *
 * Handles authentication via Azure AD.
 *
 * Azure AD Flow:
 * 1. User clicks login button
 * 2. Azure AD redirect for authentication
 * 3. Access token acquired via MSAL
 * 4. Backend API calls use Authorization: Bearer <token>
 * 5. Backend validates token via JWKS
 */

import i18next from 'i18next';
import * as azureADAuthService from '@/services/azure-ad-auth-service';
import { logInfo } from '@/lib/logger';
import { env } from '@/lib/env';

const CONTEXT = 'AuthService';

/** Backend error code for "your tenant may not use this app" (#530). */
export const TENANT_NOT_ALLOWED = 'tenant_not_allowed';

/**
 * Backend error code for "your organisation has no free seat" (#1340). Unlike
 * a refused tenant this IS a pending approval — the request was recorded — it
 * only tells the user why nothing happened on its own.
 */
export const SEAT_LIMIT_REACHED = 'seat_limit_reached';

/**
 * Backend error code for "your account is set up, but has no role yet"
 * (#1433). Every non-admin without a role waits on the login screen until a
 * supervisor assigns one; there is nothing to do in the system without it.
 */
export const NO_ROLE_ASSIGNED = 'no_role_assigned';

/** Why a known person waits, when the backend said so. */
export type PendingReason = typeof SEAT_LIMIT_REACHED | typeof NO_ROLE_ASSIGNED;

/**
 * A rejected `/auth/me`, with the parts the caller has to branch on.
 *
 * Carrying the status and the backend's error code is the point: without them
 * every rejection looked alike and the login screen offered "pending approval"
 * to people whose organisation will never be approved.
 */
export class ProfileFetchError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
    /** What the person's organisation tells people who wait (#1433). Plain text. */
    readonly organisationMessage?: string,
  ) {
    super(message);
    this.name = 'ProfileFetchError';
  }
}

function isPendingReason(code: string | undefined): code is PendingReason {
  return code === SEAT_LIMIT_REACHED || code === NO_ROLE_ASSIGNED;
}

// Storage keys
const USER_STORAGE_KEY = 'cdb_current_user';

export interface User {
  id: string;
  email: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  role?: 'admin' | 'user';
  isActive?: boolean;
  authSub?: string;
}

export interface AuthResponse {
  user: User;
  sessionId: string;
  accessToken?: string;
  message?: string;
  /**
   * Set when the backend refused for a reason no approval can lift — today
   * only `tenant_not_allowed` (#530). Distinct from an inactive user, who IS
   * waiting for an approval; the login screen must not offer one to someone
   * whose organisation was never invited.
   */
  accessDenied?: typeof TENANT_NOT_ALLOWED;
  /** Why an inactive user waits, when the backend said so (#1340, #1433). */
  pendingReason?: PendingReason;
  /** The organisation's waiting message, shown as plain text (#1433). */
  organisationMessage?: string;
}

export interface ErrorResponse {
  message: string;
  statusCode?: number;
}

// Cached user in memory
let cachedUser: User | null = null;

/**
 * Load user from localStorage
 */
function loadCachedUser(): User | null {
  try {
    const stored = localStorage.getItem(USER_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore parse errors
  }
  return null;
}

/**
 * Save user to localStorage
 */
function saveCachedUser(user: User | null): void {
  if (user) {
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
  } else {
    localStorage.removeItem(USER_STORAGE_KEY);
  }
  cachedUser = user;
}

class AuthService {
  constructor() {
    // Load cached user on init
    cachedUser = loadCachedUser();
  }

  /**
   * Start Azure AD login using redirect.
   * This will redirect the user to Microsoft login page.
   * After login, user returns and handleAzureADRedirect() should be called.
   */
  async startAzureADLogin(): Promise<void> {
    logInfo({ context: CONTEXT }, 'Starting Azure AD redirect login');
    await azureADAuthService.loginWithRedirect();
  }

  /**
   * Handle Azure AD redirect response after returning from Microsoft login.
   * Returns AuthResponse if login was successful, null if no redirect response.
   */
  async handleAzureADRedirect(): Promise<AuthResponse | null> {
    logInfo({ context: CONTEXT }, 'Checking for Azure AD redirect response');

    const result = await azureADAuthService.handleRedirectResult();

    if (!result) {
      return null;
    }

    const accessToken = result.accessToken;

    // Fetch user profile from backend
    try {
      const userInfo = await this.fetchUserProfile(accessToken);

      const user: User = {
        id: userInfo.user_id,
        email: userInfo.email,
        name: userInfo.display_name || result.user.name || result.user.email.split('@')[0],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        role: userInfo.role as 'admin' | 'user',
        isActive: userInfo.is_active,
        authSub: result.user.userId,
      };

      saveCachedUser(user);

      logInfo({ context: CONTEXT }, `Azure AD redirect login successful for ${user.email}`);

      return {
        user,
        sessionId: user.id,
        accessToken,
        message: 'login_success',
      };
    } catch (error: any) {
      // If backend rejects (403 for inactive user), create a minimal user object
      // with isActive: false so LoginPage can handle it
      return this.refusal(error, accessToken, result.user);
    }
  }

  /**
   * Ask the backend again whether a restored session may enter (#1433).
   *
   * A reload has no redirect result, so the refusal used to go unnoticed: the
   * login page saw a Microsoft token, sent a waiting person to the desktop,
   * the route gate sent them back, and the screen flickered between `/` and
   * `/desktop`. Returns null when the person may enter; only a 403 is a
   * refusal — anything else is thrown, not mistaken for a waiting room.
   */
  async checkRestoredSession(): Promise<AuthResponse | null> {
    const accessToken = azureADAuthService.getAccessToken();
    const account = await azureADAuthService.getAccountInfo();
    if (!accessToken || !account) return null;
    try {
      await this.fetchUserProfile(accessToken);
      return null;
    } catch (error) {
      if (!(error instanceof ProfileFetchError) || error.status !== 403) throw error;
      return this.refusal(error, accessToken, account);
    }
  }

  /** The login page's view of a refused `/auth/me`: refused tenant or waiting person. */
  private refusal(
    error: any,
    accessToken: string,
    account: azureADAuthService.AzureADUserInfo,
  ): AuthResponse {
    const tenantBlocked =
      error instanceof ProfileFetchError && error.code === TENANT_NOT_ALLOWED;
    logInfo(
      { context: CONTEXT },
      tenantBlocked
        ? 'Backend refused the tenant - no approval can lift this'
        : 'Backend rejected user - likely inactive or pending approval',
    );

    const user: User = {
      id: account.userId,
      email: account.email,
      name: account.name || account.email.split('@')[0],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      role: 'user',
      isActive: false,
      authSub: account.userId,
    };

    return {
      user,
      sessionId: user.id,
      accessToken,
      message: error.message || 'Account pending approval',
      ...(tenantBlocked ? { accessDenied: TENANT_NOT_ALLOWED } : {}),
      ...(error instanceof ProfileFetchError && isPendingReason(error.code)
        ? { pendingReason: error.code }
        : {}),
      ...(error instanceof ProfileFetchError && error.organisationMessage
        ? { organisationMessage: error.organisationMessage }
        : {}),
    };
  }

  /**
   * Fetch user profile from backend using the Azure AD access token.
   */
  private async fetchUserProfile(accessToken?: string): Promise<{
    user_id: string;
    email: string;
    role: string;
    is_active: boolean;
    display_name?: string;
  }> {
    const backendUrl = env.backendApiUrl;
    const headers: Record<string, string> = {};
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }
    const response = await fetch(`${backendUrl}/api/v1/auth/me`, {
      credentials: 'include',
      headers,
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({ detail: 'Unknown error' }));
      // The status code and the error code used to be dropped here, so every
      // rejection arrived at the caller as one indistinguishable Error — which
      // is why a foreign tenant was shown "pending approval" (#530). `detail`
      // is an object on the coded paths and a plain string on the legacy ones.
      const detail = body?.detail;
      const coded = typeof detail === 'object' && detail !== null;
      const err = new ProfileFetchError(
        (typeof detail === 'string' ? detail : detail?.message) ||
          'Failed to fetch user profile',
        response.status,
        coded ? detail.code : undefined,
        coded && typeof detail.organisation_message === 'string' ? detail.organisation_message : undefined,
      );
      throw err;
    }

    return response.json();
  }

  /**
   * Logout current user.
   */
  async logout(): Promise<void> {
    await azureADAuthService.logout();
    saveCachedUser(null);
    logInfo({ context: CONTEXT }, 'User logged out');
  }

  /**
   * Get current user
   */
  async getCurrentUser(): Promise<User> {
    if (cachedUser) {
      return cachedUser;
    }

    throw new Error('Nicht angemeldet');
  }

  /**
   * Check if user is authenticated.
   *
   * Tries in order:
   *   1. Existing MSAL access token (Workforce login).
   *   2. MSAL silent-restore from refresh token.
   */
  async isAuthenticated(): Promise<boolean> {
    let hasToken = false;

    if (azureADAuthService.isAuthenticated()) {
      hasToken = true;
    } else {
      const restored = await azureADAuthService.tryRestoreAuth();
      hasToken = restored;
    }

    if (hasToken) {
      if (cachedUser && !cachedUser.isActive) {
        logInfo({ context: CONTEXT }, 'Cached user is not active');
        return false;
      }
      return true;
    }

    return false;
  }

  /**
   * Get user by ID
   */
  async getUserById(id: string): Promise<User> {
    const current = await this.getCurrentUser();
    if (current.id !== id) throw new Error('Nur Self-Access verfügbar');
    return current;
  }

  /**
   * Get session ID (user ID)
   */
  getSessionId(): string | null {
    return cachedUser?.id ?? null;
  }

  /**
   * Get access token for API calls.
   */
  getAccessToken(): string | null {
    return azureADAuthService.getAccessToken();
  }

  /**
   * Refresh access token. Pass `forceRefresh=true` to bypass MSAL's
   * cache and pull a fresh token from Azure AD — required when the
   * caller actually wants to extend the session past the current
   * expiry. Default `false` is correct for restore-on-page-load and
   * 401-retry paths.
   */
  async refreshAccessToken(forceRefresh = false): Promise<string | null> {
    return await azureADAuthService.refreshAccessToken(forceRefresh);
  }

  /**
   * Clear session (triggers MSAL redirect logout).
   */
  clearSession(): void {
    azureADAuthService.logout();
    saveCachedUser(null);
  }

  /**
   * Invalidate the access token without redirect or clearing user/settings data.
   * Use when the session expired and the user should stay on the page.
   */
  clearLocalSession(): void {
    azureADAuthService.clearLocalSession();
  }

  /**
   * Clear all auth state for re-login without redirect.
   * MSAL cache is cleared so the login page shows the sign-in button.
   * The cached user is also cleared so any code that reads
   * `getCachedUser()` between logout and the new login cannot pick up
   * the prior user's identity (window-state-service keys off the user
   * id; leaving the old id resident risks the prior user's saved
   * windows leaking through if a different account signs in).
   * App settings stay in localStorage under `cdb-*` and are restored
   * by SettingsService on the next login.
   */
  async clearForRelogin(): Promise<void> {
    await azureADAuthService.clearForRelogin();
    saveCachedUser(null);
  }

  /**
   * Get cached user without async call
   */
  getCachedUser(): User | null {
    return cachedUser;
  }
}

// Singleton instance
export const authService = new AuthService();

// Convenience functions
export const {
  logout,
  getCurrentUser,
  isAuthenticated,
  getUserById,
  getSessionId,
  clearSession,
} = authService;

// Additional exports
export const getAccessToken = () => authService.getAccessToken();
export const refreshAccessToken = () => authService.refreshAccessToken();
export const getCachedUser = () => authService.getCachedUser();

/** The sign-in has ended and silent renewal failed — signing in again is the only way on. */
export class SessionExpiredError extends Error {
  constructor() {
    super(i18next.t('auth.sessionExpiredMessage'));
    this.name = 'SessionExpiredError';
  }
}

/**
 * The access token for a request, renewed first when it has lapsed (#1227).
 *
 * The periodic refresh in `useTokenRefresh` is throttled in a background tab
 * and stops while the laptop sleeps, so the first click after coming back can
 * find no valid token although the sign-in itself is still good.
 */
export async function requireAccessToken(): Promise<string> {
  const token = getAccessToken() ?? (await authService.refreshAccessToken());
  if (!token) throw new SessionExpiredError();
  return token;
}

/**
 * Try to restore authentication from localStorage on page refresh.
 */
export async function tryRestoreAuth(): Promise<boolean> {
  return await azureADAuthService.tryRestoreAuth();
}

/**
 * Get the current user ID for API requests.
 */
export function getCurrentUserId(): string | null {
  return authService.getCachedUser()?.id ?? null;
}

/**
 * Start Azure AD redirect login.
 */
export const startAzureADLogin = () => authService.startAzureADLogin();

/**
 * Handle Azure AD redirect response.
 */
export const handleAzureADRedirect = () => authService.handleAzureADRedirect();
